import React, { useEffect, useState } from "react";
import "./EmbarkDates.css";
import { Button, Card } from "@shopify/polaris";
import { Auth } from "aws-amplify";
import AsimsApiResponse from "../../types/AsimsApiResponse";
import { makeRequest } from "../../services/UploadService";
import { useDispatch, useSelector } from "react-redux";
import { PortalState } from "../../types/PortalState";
import { toast } from "react-toastify";
import { ActionTypes } from "../../store/actions/ActionTypes";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

export default function EmbarkDates() {
    const { t } = useTranslation("common");
    const currentInspection = useSelector(
        (state: PortalState) => state.portal.activeInspection
    );

    const [embarkDateTime, setEmbarkDateTime] = useState<string | undefined>(undefined);
    const [disembarkDateTime, setDisembarkDateTime] = useState<string | undefined>(undefined);
    const [dateError, setDateError] = useState<string | undefined>();
    const [checklistSectionCompleted, setChecklistSectionCompleted] = useState<boolean>(false);

    useEffect(() => {
        if (currentInspection?.SurveyorCompletionStatus?.EmbarkDate) {
            setEmbarkDateTime(currentInspection?.SurveyorCompletionStatus?.EmbarkDate);
        }

        if (currentInspection?.SurveyorCompletionStatus?.DisembarkDate) {
            setDisembarkDateTime(currentInspection?.SurveyorCompletionStatus?.DisembarkDate);
        }

        if (currentInspection?.SurveyorCompletionStatus?.ChecklistComplete) {
            setChecklistSectionCompleted(currentInspection?.SurveyorCompletionStatus?.ChecklistComplete);
        }
    }, [currentInspection?.SurveyorCompletionStatus])

    const dispatch = useDispatch();

    const handleEmbarkDateTimeChange = (event: any) => {
        if (disembarkDateTime && new Date(event.target.value) > new Date(disembarkDateTime)) {
            setDateError(t("embarkDates.embarkValidationError"));
        } else {
            setDateError(undefined);
        }

        setEmbarkDateTime(event.target.value);

        const surveyorCompletionStatus = currentInspection?.SurveyorCompletionStatus;
        if (surveyorCompletionStatus) {
            surveyorCompletionStatus.EmbarkDate = event.target.value;
            dispatch({
                type: ActionTypes.UPDATE_COMPLETION_STATUS,
                surveyorCompletionStatus: surveyorCompletionStatus
            });
        }
    };

    const handleDisembarkDateTimeChange = (event: any) => {
        if (embarkDateTime && new Date(event.target.value) < new Date(embarkDateTime)) {
            setDateError(t("embarkDates.disembarkValidationError"));
        } else {
            setDateError(undefined);
        }

        setDisembarkDateTime(event.target.value);

        const surveyorCompletionStatus = currentInspection?.SurveyorCompletionStatus;
        if (surveyorCompletionStatus) {
            surveyorCompletionStatus.DisembarkDate = event.target.value;
            dispatch({
                type: ActionTypes.UPDATE_COMPLETION_STATUS,
                surveyorCompletionStatus: surveyorCompletionStatus
            });
        }
    };

    const handleSubmit = async () => {
        if (dateError) {
            return;
        }

        if (!embarkDateTime) {
            setDateError(t("embarkDates.requiredEmbarkDate"));
            return;
        }

        const currentSeession = await Auth.currentSession();
        const idToken = currentSeession.getIdToken().getJwtToken();

        ReactGA.event({
            category: "Embark Dates",
            action: "Saving",
            label: "Embark Dates",
            value: 0
        });

        const result: AsimsApiResponse = await makeRequest(
            {
                url: "/inspection/embark",
                method: "post",
                data: {
                    inspectionId: currentInspection?.Id.toLowerCase(),
                    embarkDate: embarkDateTime,
                    disembarkDate: disembarkDateTime
                }
            },
            idToken
        )

        if (!result.success) {
            toast.error(t("embarkDates.updateFailed"));
            ReactGA.event({
                category: "Embark Dates",
                action: "Failed",
                label: "Embark Dates",
                value: 0
            });
        }

        const surveyorCompletionStatus = currentInspection?.SurveyorCompletionStatus;

        if (surveyorCompletionStatus) {
            surveyorCompletionStatus.EmbarkDate = embarkDateTime;
            surveyorCompletionStatus.DisembarkDate = disembarkDateTime;
            dispatch({
                type: ActionTypes.UPDATE_COMPLETION_STATUS,
                surveyorCompletionStatus: surveyorCompletionStatus
            });
            toast.success(t("embarkDates.updateSuccess"));
            ReactGA.event({
                category: "Embark Dates",
                action: "Saved",
                label: "Embark Dates",
                value: 0
            });
        }
    }

    return (
        <Card>
            <div className="embark-dates">
                <div className="date-fields">
                    <div className="date-fields" id="embark">
                        <label htmlFor="embark-date-time">{t("embarkDates.embark")}</label>
                        <input
                            type="datetime-local"
                            id="embark-date-time"
                            className="date-input-embark"
                            name="embark-date-time"
                            value={embarkDateTime ?? ""}
                            onChange={handleEmbarkDateTimeChange}
                            disabled={checklistSectionCompleted}/>
                    </div>
                    <div className="date-fields" id="disembark">
                        <label htmlFor="disembark-date-time">{t("embarkDates.disembark")}</label>
                        <input
                            type="datetime-local"
                            id="disembark-date-time"
                            className="date-input-embark"
                            name="disembark-date-time"
                            value={disembarkDateTime ?? ""}
                            onChange={handleDisembarkDateTimeChange}
                            disabled={checklistSectionCompleted}/>
                    </div>
                </div>
                <div className="submit-section">
                    <Button submit size="large" onClick={handleSubmit}
                            disabled={checklistSectionCompleted}>{t("embarkDates.submit")}</Button>
                </div>

            </div>
            <div className={"submissionNotes"}>
                {t("embarkDates.submissionNotes")}
            </div>
            {dateError && <div className="error-message"><p className="error">{dateError}</p></div>}
        </Card>
    )
}